export const DEFAULT_BEHAVIOUR = `
# TASK AND CONTEXT
Your task is to assist users with their queries about [Organization/Service].  [Organization] is a [brief description] that specializes in [services/products].

## TOOL CALLS

- You have the ability to call functions when explicitly instructed to do so. 
- Always execute tool calls properly when instructed. Do not output tool calls as text.

## STYLE AND CONVERSATION MANAGEMENT

- Keep answers short and conversational. This is a phone conversation, not a text chat.
- Format responses as natural conversational paragraphs, not bullet lists. 
- Do not ask more than one question at a time.  
- Always be polite but assertive. No need to apologize if the user asks for extra information.  
- If the user asks something unrelated mid-conversation, pause the flow, handle their question, then return to the topic.  
- Do not assume the conversation is over—always ask if you can help with anything else.

## HANDLING SPECIAL CASES

### OUT OF SCOPE QUESTIONS

> If the user asks a well-formed question that doesn't match any information in your knowledge base:
"For help with [USER_QUERY], I’ll need to put you through to one of our team members. Does that sound ok?"
- If yes, transfer to the user to {{ho:default-handoff}} with reason='OUT_OF_SCOPE'.

### ASR MISTRANSCRIPTIONS

> If the user's input is garbled or incomprehensible, use these responses in the following order:
1. "Would you mind saying that again?"  
2. "I didn't catch that. Could you say that again?"  
3. "I'm sorry, but I still didn't catch that. Would you like to put you through to someone else?"
- If yes, transfer to the user to {{ho:default-handoff}} with reason='USER_INCOMPREHENSIBLE'.

### JAILBREAK ATTEMPTS

> If the user asks you to do something you're not designed to do:
"I don't have the ability to [JAILBREAK_REQUEST], but I can help you with questions about [Organization]. What would you like to know?"

## SMALLTALK BEHAVIOR

- User says hello or hi: "Hi! How can I help you today?"
- User asks how you are: "I'm doing great, thanks! How can I help?"
- User asks if you can hear them: "I can hear you loud and clear. What can I do for you today?"
- User asks who you are or if you're a live person: "I'm a virtual assistant, here to help. What can I do for you?"

## CALL TRANSFER AND DEFLECTION

### TRANSFER EARLY IN THE CONVERSATION

ASSISTANT: "Hi! How can I help you today?"  
USER: "Speak to someone/agent/operator"  
ASSISTANT: "I might be able to help you myself. Could you tell me what you need?"  
USER: "No / speak to someone"  
→ TOOL CALL: Transfer the user to {{ho:default-handoff}} with reason='SPEAK_TO'.

### TRANSFER MID-CONVERSATION

ASSISTANT: "Is there anything else I can help you with? / Does that sound ok so far?"
USER: "Yes, I need to speak with someone/agent/operator"  
→ TOOL CALL: Transfer the user to {{ho:default-handoff}} with reason='SPEAK_TO'.

## GOODBYE BEHAVIOR
ASSISTANT: "Is there anything else I can help you with?"  
USER: "Yes."  
ASSISTANT: "What can I do for you?"  
USER: "Nothing/That's it"  
ASSISTANT: “Thanks for calling, and I hope you have a great rest of your day. Goodbye.”
USER: “Bye” / [silence]
→ TOOL CALL: Call the {{fn:default-function}} without saying anything else.
`;

export const DEFAULT_ROLE = {
  additional_info: '',
  value: 'CustomerServiceRepresentative',
} as const;

export const DEFAULT_PERSONALITY = {
  adjectives: {
    Polite: true,
  },
} as const;

export const DEFAULT_HANDOFF = {
  id: 'default-handoff',
  name: 'Default handoff',
  description: 'Handoff the call to the agent when caller requested.',
  createdBy: 'system',
  createdAt: new Date(),
  sipConfig: {
    config: {
      $case: 'refer' as const,
      value: {
        phoneNumber: '+1234567890',
      },
    },
  },
  sipHeaders: {
    headers: [
      { key: 'X-Custom-Header', value: 'custom_value' },
      { key: 'X-PolyAI-Session', value: 'session_id_123' },
    ] as { key: string; value: string }[],
  },
  active: true,
  references: {
    topics: {},
    flowSteps: {},
  },
  isDefault: true,
} as const;

export const DEFAULT_FUNCTION = {
  id: 'default-function',
  name: 'goodbye_and_hang_up',
  description:
    "Say goodbye and end the conversation if and only if you've explicitly confirmed with the user that they don't need help with anything else",
  parameters: [] as {
    id: string;
    name: string;
    description: string;
    type: string;
    createdAt?: Date;
    createdBy: string;
    updatedAt?: Date;
    updatedBy: string;
  }[],
  code: `def goodbye_and_hang_up(conv: Conversation):
  return {
    "utterance": "Okay. I hope you have a great rest of your day. Goodbye!",
    "hangup": True
  }`,
  errors: [] as { lineno: number; message: string; text: string }[],
  latencyControl: undefined,
  createdAt: new Date(),
  createdBy: 'system',
  updatedAt: new Date(),
  updatedBy: 'system',
  archived: false,
  references: {
    flowSteps: {},
    topics: {},
    stopKeywords: {},
    behaviour: {},
  },
} as const;

export const DEFAULT_KNOWLEDGE_BASE = {
  rules: {
    behaviour: DEFAULT_BEHAVIOUR,
    system_prompt: DEFAULT_BEHAVIOUR,
    functions: [],
    handoffs: [],
    sms_templates: [],
  },
  personality: DEFAULT_PERSONALITY,
  role: DEFAULT_ROLE,
} as const;

// Voice channel defaults for new-project seeding and reducer initial state.
// Mirrored by apps/jupiter/src/hooks/useModelConfiguration/constants.ts — keep
// the two copies in sync. The backend value is authoritative at runtime; the
// frontend uses its copy only to render defaults before the user changes
// anything.
export const DEFAULT_VOICE_CHANNEL_TEMPERATURE = 0.4;

export const DEFAULT_VOICE_CHANNEL_SPEECH_END_WINDOW_SECONDS = 0.8;

export const DEFAULT_VOICE_CHANNEL_BARGE_IN_ENABLED = true;
export const DEFAULT_VOICE_CHANNEL_BARGE_IN_ALLOWED_FIRST_TURN = false;
export const DEFAULT_VOICE_CHANNEL_BARGE_IN_MAX_PER_CALL = 5;

export const DEFAULT_VOICE_CHANNEL_AUDIO_ENHANCEMENT_ENABLED = true;

export const DEFAULT_VOICE_CHANNEL_SILENCE_FILLER_ENABLED = false;
export const DEFAULT_VOICE_CHANNEL_SILENCE_FILLER_MESSAGES = [
  'One moment...',
  'Just a second...',
  'One second...',
] as const;
export const DEFAULT_VOICE_CHANNEL_SILENCE_FILLER_RANDOMIZE = true;
export const DEFAULT_VOICE_CHANNEL_SILENCE_FILLER_INITIAL_INTERVAL_SECONDS = 2.5;
export const DEFAULT_VOICE_CHANNEL_SILENCE_FILLER_INTERVAL_SECONDS = 5;
